/**
 * Offline Storage Service - ÑANDUTÍ
 *
 * v2.1.0 - Offline-First Audit PWA Architecture
 *
 * Implements:
 * - IndexedDB storage for offline data
 * - Sync queue for pending mutations
 * - Conflict resolution strategies
 * - Background sync via Service Worker
 *
 * Named after the traditional Paraguayan lace (ñandutí),
 * representing the intricate web of data connections.
 */

// ============================================
// TYPES
// ============================================

export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'conflict' | 'error';

export interface SyncableRecord {
  id: string;
  _localId?: string;
  _syncStatus: SyncStatus;
  _lastModified: string;
  _serverVersion?: number;
  _localVersion?: number;
}

export interface SyncQueueItem {
  id: string;
  table: string;
  operation: 'create' | 'update' | 'delete';
  data: Record<string, unknown>;
  timestamp: string;
  retryCount: number;
  lastError?: string;
}

export interface ConflictResolution {
  strategy: 'client_wins' | 'server_wins' | 'merge' | 'manual';
  resolvedData?: Record<string, unknown>;
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface OfflineConfig {
  dbName: string;
  dbVersion: number;
  stores: StoreConfig[];
  syncInterval: number; // ms
  maxRetries: number;
  conflictStrategy: ConflictResolution['strategy'];
}

export interface StoreConfig {
  name: string;
  keyPath: string;
  indexes: { name: string; keyPath: string | string[]; unique?: boolean }[];
  syncEnabled: boolean;
}

// ============================================
// DEFAULT CONFIGURATION
// ============================================

export const DEFAULT_OFFLINE_CONFIG: OfflineConfig = {
  dbName: 'OmniTowerOffline',
  dbVersion: 1,
  stores: [
    {
      name: 'audits',
      keyPath: 'id',
      indexes: [
        { name: 'by_store', keyPath: 'storeId' },
        { name: 'by_status', keyPath: '_syncStatus' },
        { name: 'by_date', keyPath: 'createdAt' },
      ],
      syncEnabled: true,
    },
    {
      name: 'audit_items',
      keyPath: 'id',
      indexes: [
        { name: 'by_audit', keyPath: 'auditId' },
        { name: 'by_sku', keyPath: 'sku' },
      ],
      syncEnabled: true,
    },
    {
      name: 'products',
      keyPath: 'id',
      indexes: [
        { name: 'by_sku', keyPath: 'sku', unique: true },
        { name: 'by_category', keyPath: 'category' },
      ],
      syncEnabled: false, // Read-only cache
    },
    {
      name: 'stores',
      keyPath: 'id',
      indexes: [
        { name: 'by_name', keyPath: 'name' },
      ],
      syncEnabled: false,
    },
    {
      name: 'sync_queue',
      keyPath: 'id',
      indexes: [
        { name: 'by_table', keyPath: 'table' },
        { name: 'by_timestamp', keyPath: 'timestamp' },
      ],
      syncEnabled: false,
    },
  ],
  syncInterval: 30000, // 30 seconds
  maxRetries: 5,
  conflictStrategy: 'client_wins', // For field auditors, their data takes precedence
};

// ============================================
// INDEXEDDB MANAGER
// ============================================

let db: IDBDatabase | null = null;

/**
 * Initialize IndexedDB.
 */
export async function initOfflineDB(config = DEFAULT_OFFLINE_CONFIG): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(config.dbName, config.dbVersion);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('IndexedDB initialized:', config.dbName);
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      for (const store of config.stores) {
        if (!database.objectStoreNames.contains(store.name)) {
          const objectStore = database.createObjectStore(store.name, {
            keyPath: store.keyPath,
          });

          for (const index of store.indexes) {
            objectStore.createIndex(index.name, index.keyPath, {
              unique: index.unique ?? false,
            });
          }
        }
      }
    };
  });
}

/**
 * Get the database instance.
 */
export function getDB(): IDBDatabase | null {
  return db;
}

/**
 * Close the database connection.
 */
export function closeDB(): void {
  if (db) {
    db.close();
    db = null;
  }
}

// ============================================
// GENERIC CRUD OPERATIONS
// ============================================

/**
 * Get a record by ID.
 */
export async function getRecord<T>(
  storeName: string,
  id: string
): Promise<T | null> {
  const database = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all records from a store.
 */
export async function getAllRecords<T>(storeName: string): Promise<T[]> {
  const database = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get records by index.
 */
export async function getByIndex<T>(
  storeName: string,
  indexName: string,
  value: IDBValidKey
): Promise<T[]> {
  const database = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Put a record (insert or update).
 */
export async function putRecord<T extends { id: string }>(
  storeName: string,
  record: T,
  addToSyncQueue = true
): Promise<T> {
  const database = await initOfflineDB();

  // Add sync metadata
  const syncableRecord = {
    ...record,
    _syncStatus: 'pending' as SyncStatus,
    _lastModified: new Date().toISOString(),
    _localVersion: ((record as T & { _localVersion?: number })._localVersion || 0) + 1,
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName, 'sync_queue'], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(syncableRecord);

    request.onsuccess = async () => {
      if (addToSyncQueue) {
        await addToSyncQueueInternal(transaction, storeName, 'update', syncableRecord);
      }
      resolve(syncableRecord as T);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a record.
 */
export async function deleteRecord(
  storeName: string,
  id: string,
  addToSyncQueue = true
): Promise<void> {
  const database = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName, 'sync_queue'], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = async () => {
      if (addToSyncQueue) {
        await addToSyncQueueInternal(transaction, storeName, 'delete', { id });
      }
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all records from a store.
 */
export async function clearStore(storeName: string): Promise<void> {
  const database = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ============================================
// SYNC QUEUE MANAGEMENT
// ============================================

async function addToSyncQueueInternal(
  transaction: IDBTransaction,
  table: string,
  operation: SyncQueueItem['operation'],
  data: Record<string, unknown>
): Promise<void> {
  const queueStore = transaction.objectStore('sync_queue');

  const item: SyncQueueItem = {
    id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    table,
    operation,
    data,
    timestamp: new Date().toISOString(),
    retryCount: 0,
  };

  queueStore.put(item);
}

/**
 * Get all pending sync items.
 */
export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  return getAllRecords<SyncQueueItem>('sync_queue');
}

/**
 * Remove item from sync queue.
 */
export async function removeSyncQueueItem(id: string): Promise<void> {
  return deleteRecord('sync_queue', id, false);
}

/**
 * Update sync queue item (for retry tracking).
 */
export async function updateSyncQueueItem(item: SyncQueueItem): Promise<void> {
  const database = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction('sync_queue', 'readwrite');
    const store = transaction.objectStore('sync_queue');
    const request = store.put(item);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ============================================
// OFFLINE SYNC SERVICE
// ============================================

export const offlineSyncService = {
  /**
   * Check if online.
   */
  isOnline: (): boolean => {
    return navigator.onLine;
  },

  /**
   * Get pending changes count.
   */
  getPendingCount: async (): Promise<number> => {
    const queue = await getSyncQueue();
    return queue.length;
  },

  /**
   * Sync all pending changes to server.
   */
  syncToServer: async (
    syncFn: (item: SyncQueueItem) => Promise<{ success: boolean; error?: string }>
  ): Promise<{ synced: number; failed: number; remaining: number }> => {
    if (!offlineSyncService.isOnline()) {
      const queue = await getSyncQueue();
      return { synced: 0, failed: 0, remaining: queue.length };
    }

    const queue = await getSyncQueue();
    let synced = 0;
    let failed = 0;

    for (const item of queue) {
      try {
        const result = await syncFn(item);

        if (result.success) {
          await removeSyncQueueItem(item.id);
          synced++;

          // Update record sync status
          const record = await getRecord<SyncableRecord>(item.table, item.data.id as string);
          if (record) {
            record._syncStatus = 'synced';
            await putRecord(item.table, record, false);
          }
        } else {
          item.retryCount++;
          item.lastError = result.error;

          if (item.retryCount >= DEFAULT_OFFLINE_CONFIG.maxRetries) {
            // Mark as error
            await updateSyncQueueItem({ ...item, retryCount: -1 }); // -1 = permanent failure
            failed++;
          } else {
            await updateSyncQueueItem(item);
          }
        }
      } catch (e) {
        item.retryCount++;
        item.lastError = e instanceof Error ? e.message : 'Unknown error';
        await updateSyncQueueItem(item);
        failed++;
      }
    }

    const remainingQueue = await getSyncQueue();
    return { synced, failed, remaining: remainingQueue.filter(i => i.retryCount >= 0).length };
  },

  /**
   * Download data for offline use.
   */
  downloadForOffline: async <T extends { id: string }>(
    storeName: string,
    data: T[]
  ): Promise<void> => {
    const database = await initOfflineDB();

    return new Promise((resolve, reject) => {
      const transaction = database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      for (const record of data) {
        store.put({
          ...record,
          _syncStatus: 'synced' as SyncStatus,
          _lastModified: new Date().toISOString(),
        });
      }

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  },

  /**
   * Clear all offline data.
   */
  clearAllData: async (): Promise<void> => {
    for (const store of DEFAULT_OFFLINE_CONFIG.stores) {
      await clearStore(store.name);
    }
  },

  /**
   * Get storage usage info.
   */
  getStorageInfo: async (): Promise<{
    used: number;
    quota: number;
    percentage: number;
  }> => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        quota: estimate.quota || 0,
        percentage: estimate.quota ? Math.round((estimate.usage || 0) / estimate.quota * 100) : 0,
      };
    }

    return { used: 0, quota: 0, percentage: 0 };
  },
};

// ============================================
// AUDIT-SPECIFIC OFFLINE FUNCTIONS
// ============================================

export interface OfflineAudit {
  id: string;
  storeId: string;
  storeName: string;
  auditorId: string;
  auditorName: string;
  type: 'inventory_count' | 'receiving' | 'compliance';
  status: 'draft' | 'in_progress' | 'completed' | 'submitted';
  startedAt: string;
  completedAt?: string;
  notes?: string;
  gpsLocation?: { lat: number; lng: number };
  items: OfflineAuditItem[];
  _syncStatus: SyncStatus;
  _lastModified: string;
}

export interface OfflineAuditItem {
  id: string;
  auditId: string;
  sku: string;
  productName: string;
  expectedQty: number;
  countedQty: number;
  location?: string;
  photoUrl?: string;
  voiceNoteUrl?: string;
  notes?: string;
  timestamp: string;
}

export const offlineAuditService = {
  /**
   * Create a new offline audit.
   */
  createAudit: async (audit: Omit<OfflineAudit, 'id' | '_syncStatus' | '_lastModified'>): Promise<OfflineAudit> => {
    const newAudit: OfflineAudit = {
      ...audit,
      id: `audit-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      _syncStatus: 'pending',
      _lastModified: new Date().toISOString(),
    };

    await putRecord('audits', newAudit);
    return newAudit;
  },

  /**
   * Get audit by ID.
   */
  getAudit: async (id: string): Promise<OfflineAudit | null> => {
    return getRecord<OfflineAudit>('audits', id);
  },

  /**
   * Get all audits for a store.
   */
  getAuditsByStore: async (storeId: string): Promise<OfflineAudit[]> => {
    return getByIndex<OfflineAudit>('audits', 'by_store', storeId);
  },

  /**
   * Get pending (unsynced) audits.
   */
  getPendingAudits: async (): Promise<OfflineAudit[]> => {
    return getByIndex<OfflineAudit>('audits', 'by_status', 'pending');
  },

  /**
   * Update an audit.
   */
  updateAudit: async (audit: OfflineAudit): Promise<OfflineAudit> => {
    return putRecord('audits', audit);
  },

  /**
   * Add item to audit.
   */
  addAuditItem: async (item: Omit<OfflineAuditItem, 'id' | 'timestamp'>): Promise<OfflineAuditItem> => {
    const newItem: OfflineAuditItem = {
      ...item,
      id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: new Date().toISOString(),
    };

    await putRecord('audit_items', newItem);
    return newItem;
  },

  /**
   * Get items for an audit.
   */
  getAuditItems: async (auditId: string): Promise<OfflineAuditItem[]> => {
    return getByIndex<OfflineAuditItem>('audit_items', 'by_audit', auditId);
  },

  /**
   * Update audit item.
   */
  updateAuditItem: async (item: OfflineAuditItem): Promise<OfflineAuditItem> => {
    return putRecord('audit_items', item);
  },

  /**
   * Download audit route for offline work.
   * Downloads products, stores, and pending audits.
   */
  downloadAuditRoute: async (
    storeIds: string[],
    products: { id: string; sku: string; name: string; category: string; expectedQty?: number }[],
    stores: { id: string; name: string; location: string }[]
  ): Promise<{ products: number; stores: number }> => {
    await offlineSyncService.downloadForOffline('products', products);
    await offlineSyncService.downloadForOffline('stores', stores);

    return {
      products: products.length,
      stores: stores.length,
    };
  },

  /**
   * Get download status.
   */
  getDownloadStatus: async (): Promise<{
    products: number;
    stores: number;
    pendingAudits: number;
    lastSynced?: string;
  }> => {
    const products = await getAllRecords('products');
    const stores = await getAllRecords('stores');
    const pendingAudits = await offlineAuditService.getPendingAudits();

    return {
      products: products.length,
      stores: stores.length,
      pendingAudits: pendingAudits.length,
    };
  },
};

// ============================================
// SERVICE WORKER REGISTRATION
// ============================================

/**
 * Register the service worker for offline support.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('Service worker registered:', registration.scope);

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content available
            console.log('New content available; please refresh.');
          }
        });
      }
    });

    return registration;
  } catch (e) {
    console.error('Service worker registration failed:', e);
    return null;
  }
}

/**
 * Request background sync.
 */
export async function requestBackgroundSync(tag = 'sync-audits'): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('SyncManager' in window)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    // Background Sync API - not in all TS libs yet
    await (registration as ServiceWorkerRegistration & { sync: { register(tag: string): Promise<void> } }).sync.register(tag);
    return true;
  } catch (e) {
    console.error('Background sync registration failed:', e);
    return false;
  }
}

// ============================================
// POST-LOGIN HANDSHAKE
// ============================================

export interface HandshakeResult {
  success: boolean;
  serviceWorkerRegistered: boolean;
  indexedDBInitialized: boolean;
  criticalDataSynced: boolean;
  syncedItems: {
    stores: number;
    products: number;
  };
  errors: string[];
}

/**
 * Post-login handshake that initializes offline capabilities.
 * Should be called immediately after successful authentication.
 *
 * This function:
 * 1. Initializes IndexedDB for offline storage
 * 2. Registers the Service Worker if not active
 * 3. Syncs critical route data for immediate offline use
 */
export async function performPostLoginHandshake(
  tenantId: string,
  userId: string,
  options: {
    syncStores?: boolean;
    syncProducts?: boolean;
    forceServiceWorkerUpdate?: boolean;
  } = {}
): Promise<HandshakeResult> {
  const result: HandshakeResult = {
    success: false,
    serviceWorkerRegistered: false,
    indexedDBInitialized: false,
    criticalDataSynced: false,
    syncedItems: {
      stores: 0,
      products: 0,
    },
    errors: [],
  };

  const { syncStores = true, syncProducts = false, forceServiceWorkerUpdate = false } = options;

  console.log(`[Handshake] Starting post-login handshake for tenant: ${tenantId}, user: ${userId}`);

  // Step 1: Initialize IndexedDB
  try {
    await initOfflineDB();
    result.indexedDBInitialized = true;
    console.log('[Handshake] IndexedDB initialized successfully');
  } catch (err) {
    const errorMsg = `IndexedDB initialization failed: ${err instanceof Error ? err.message : 'Unknown error'}`;
    result.errors.push(errorMsg);
    console.error('[Handshake]', errorMsg);
  }

  // Step 2: Register Service Worker
  try {
    const registration = await registerServiceWorker();
    result.serviceWorkerRegistered = registration !== null;

    if (registration) {
      console.log('[Handshake] Service Worker registered:', registration.scope);

      // Force update if requested
      if (forceServiceWorkerUpdate) {
        await registration.update();
        console.log('[Handshake] Service Worker update triggered');
      }
    }
  } catch (err) {
    const errorMsg = `Service Worker registration failed: ${err instanceof Error ? err.message : 'Unknown error'}`;
    result.errors.push(errorMsg);
    console.error('[Handshake]', errorMsg);
  }

  // Step 3: Sync critical data if online
  if (offlineSyncService.isOnline()) {
    try {
      // Import supabase here to avoid circular dependencies
      const { supabase } = await import('./supabase');

      // Sync stores for this tenant
      if (syncStores) {
        const { data: storesData, error: storesError } = await supabase
          .from('stores')
          .select('id, name, location')
          .eq('tenant_id', tenantId);

        if (storesError) {
          result.errors.push(`Store sync failed: ${storesError.message}`);
        } else if (storesData) {
          await offlineSyncService.downloadForOffline('stores', storesData);
          result.syncedItems.stores = storesData.length;
          console.log(`[Handshake] Synced ${storesData.length} stores for offline use`);
        }
      }

      // Sync product catalog (optional, can be large)
      if (syncProducts) {
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('id, sku, name, category')
          .eq('tenant_id', tenantId)
          .limit(1000); // Limit initial sync to 1000 products

        if (productsError) {
          result.errors.push(`Product sync failed: ${productsError.message}`);
        } else if (productsData) {
          await offlineSyncService.downloadForOffline('products', productsData);
          result.syncedItems.products = productsData.length;
          console.log(`[Handshake] Synced ${productsData.length} products for offline use`);
        }
      }

      result.criticalDataSynced = result.syncedItems.stores > 0;
    } catch (err) {
      const errorMsg = `Critical data sync failed: ${err instanceof Error ? err.message : 'Unknown error'}`;
      result.errors.push(errorMsg);
      console.error('[Handshake]', errorMsg);
    }
  } else {
    console.log('[Handshake] Offline - skipping data sync');
  }

  // Determine overall success
  result.success = result.indexedDBInitialized && result.errors.length === 0;

  console.log('[Handshake] Post-login handshake complete:', {
    success: result.success,
    serviceWorker: result.serviceWorkerRegistered,
    indexedDB: result.indexedDBInitialized,
    dataSynced: result.criticalDataSynced,
    errors: result.errors.length,
  });

  return result;
}

/**
 * Check if the app is ready for offline use.
 * Returns true if IndexedDB is initialized and Service Worker is active.
 */
export async function isOfflineReady(): Promise<boolean> {
  try {
    // Check IndexedDB
    const dbReady = getDB() !== null;

    // Check Service Worker
    const swReady = 'serviceWorker' in navigator &&
      navigator.serviceWorker.controller !== null;

    return dbReady && swReady;
  } catch {
    return false;
  }
}

/**
 * Get offline readiness status for debugging.
 */
export async function getOfflineReadinessStatus(): Promise<{
  indexedDB: boolean;
  serviceWorker: boolean;
  online: boolean;
  pendingSync: number;
  storageUsed: number;
  storageQuota: number;
}> {
  const storageInfo = await offlineSyncService.getStorageInfo();
  const pendingCount = await offlineSyncService.getPendingCount();

  return {
    indexedDB: getDB() !== null,
    serviceWorker: 'serviceWorker' in navigator && navigator.serviceWorker.controller !== null,
    online: offlineSyncService.isOnline(),
    pendingSync: pendingCount,
    storageUsed: storageInfo.used,
    storageQuota: storageInfo.quota,
  };
}

export default offlineSyncService;
